import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useEffect, useRef, useCallback } from 'react';
import { JobStatus } from 'libs.jobs.common';
import { useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { useJobs } from '../hooks/use_jobs.hook';
import { useJobsAck } from '../hooks/use_jobs_ack.hook';
export const JobsContext = createContext(null);
export const JobsProvider = ({ children }) => {
    const { isLoading, values, error } = useJobs({ status: [JobStatus.ERROR, JobStatus.DONE] });
    const ack = useJobsAck();
    const { addNotification } = useToastNotification();
    const translate = useLocalizeMessage();
    const isAck = useRef({});
    const jobHandlers = useRef(new Map());
    const defaultHandler = useCallback((completion_message) => {
        var _a;
        const title = translate((_a = completion_message === null || completion_message === void 0 ? void 0 : completion_message.message_key) !== null && _a !== void 0 ? _a : 'Success');
        addNotification({ title, type: 'success' });
    }, [translate, addNotification]);
    const defaultErrorHandler = useCallback((error_message) => {
        var _a;
        const title = translate((_a = error_message === null || error_message === void 0 ? void 0 : error_message.message_key) !== null && _a !== void 0 ? _a : 'Error');
        addNotification({ title, type: 'error' });
    }, [translate, addNotification]);
    const addJobHandler = useCallback((jobId, handler) => {
        var _a;
        const handlers = (_a = jobHandlers.current.get(jobId)) !== null && _a !== void 0 ? _a : [];
        handlers.push(handler);
        jobHandlers.current.set(jobId, handlers);
    }, []);
    const removeJobHandler = useCallback((jobId) => {
        jobHandlers.current.delete(jobId);
    }, []);
    useEffect(() => {
        if (isLoading || error || !values) {
            return;
        }
        for (const { jobId, status, completion_message, error_message } of values) {
            if (isAck.current[jobId]) {
                continue;
            }
            isAck.current[jobId] = true;
            if (status === JobStatus.DONE) {
                // Execute handlers for this job
                const handlers = jobHandlers.current.get(jobId);
                if (handlers) {
                    handlers.forEach((handler) => { var _a; return (_a = handler.onDone) === null || _a === void 0 ? void 0 : _a.call(handler, completion_message); });
                    // Clear handlers after execution
                    jobHandlers.current.delete(jobId);
                }
                else {
                    defaultHandler(completion_message);
                }
            }
            if (status === JobStatus.ERROR) {
                const handlers = jobHandlers.current.get(jobId);
                if (handlers) {
                    handlers.forEach((handler) => { var _a; return (_a = handler.onError) === null || _a === void 0 ? void 0 : _a.call(handler, error_message); });
                    // Clear handlers after execution
                    jobHandlers.current.delete(jobId);
                }
                else {
                    defaultErrorHandler(error_message);
                }
            }
            ack(jobId);
        }
    }, [isLoading, values, error, ack, addNotification, translate, defaultHandler, defaultErrorHandler]);
    return (_jsx(JobsContext.Provider, { value: {
            addJobHandler,
            removeJobHandler,
        }, children: children }));
};
//# sourceMappingURL=jobs_context.js.map